import { FC, MouseEvent, useEffect, useState } from "react";
import {
  Box,
  Card,
  IconButton,
  Link as MuiLink,
  makeStyles,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@material-ui/core";
import { Settings as SettingsIcon } from "@material-ui/icons";
import { map, some } from "lodash";
import moment from "moment";
import { useLocation } from "react-router";
import { Link, useHistory } from "react-router-dom";

import { AccountInfo, AlertActionArea, CardActionArea } from "components";
import { DASHED_DATE_FORMAT, EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { EditRoyaltyAccountModal } from "containers";
import { FormValues } from "containers/CredentialsForm";
import { queryClient } from "containers/Root";
import {
  formatAmountCompact,
  formatCurrencyAmount,
  formatCurrencyString,
} from "lib/format";
import * as routes from "routes";
import { updateRoyaltyAccountPassword } from "services/assets";
import { AssetKYWRoyaltyAccount } from "services/assets/types";
import { RoyaltyAccount } from "services/royaltyAccount/types";
import { RoyaltyPayor } from "services/royaltyPayor/types";
import theme, { Theme } from "theme";
import { thinBorderStyles } from "theme/constants";

const useStyles = makeStyles<Theme, { isSmDown: boolean }>(() => ({
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: props => (props.isSmDown ? "unset" : "245px"),
    padding: "20px",
  },
  actionAreaContainer: thinBorderStyles,
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  menuItemLink: {
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.custom.palette.purple.lightest,
    },
  },
  editAccountBtn: {
    "&:hover": {
      backgroundColor: theme.custom.palette.purple.lightest,
    },
  },
  royaltyInfos: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
  },
  alertMessage: {
    "& .MuiAlert-message": {
      flexGrow: 1,
    },
  },
  link: {
    cursor: "pointer",
    color: theme.custom.palette.blue.main,
    fontSize: "0.875rem",
    "&:hover": {
      textDecoration: "none",
    },
  },
  arrowIcon: {
    fontSize: "0.875rem",
    verticalAlign: "middle",
    color: theme.custom.palette.blue.main,
  },
  alertFontSize: {
    fontSize: "0.875rem",
  },
}));

export interface RoyaltyAccountCardProps {
  assets: AssetKYWRoyaltyAccount[];
  royaltyAccount?: RoyaltyAccount;
  royaltyPayor: RoyaltyPayor;
}

const RoyaltyAccountCard: FC<RoyaltyAccountCardProps> = ({
  assets,
  royaltyAccount,
  royaltyPayor,
}) => {
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const location = useLocation();
  const classes = useStyles({ isSmDown });
  const history = useHistory();
  const urlSearchParams = new URLSearchParams(location.search);
  const editAssetId = urlSearchParams.get("edit_asset_id");

  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const [
    isEditRoyaltyAccountModalOpen,
    setIsEditRoyaltyAccountModalOpen,
  ] = useState(false);

  const assetIds = map(assets, asset => asset.id);
  useEffect(() => {
    if (
      !isEditRoyaltyAccountModalOpen &&
      editAssetId &&
      assetIds.includes(Number(editAssetId))
    ) {
      setIsEditRoyaltyAccountModalOpen(true);
    }
  }, [
    editAssetId,
    isEditRoyaltyAccountModalOpen,
    assetIds,
    setIsEditRoyaltyAccountModalOpen,
  ]);

  const lastAsset = assets[0];
  const isConnected = [
    "Royalty Information Up To Date",
    "Connection Lost - Temporary outage",
  ].includes(lastAsset.last_scraper_task_status?.message);

  const lastValuationResult = lastAsset.full_catalog_valuation_result;

  const isMd = useMediaQuery(theme.breakpoints.only("md"));

  const valuationStats = [
    {
      label: "LTM",
      value: !!lastValuationResult?.ltm
        ? isMd
          ? formatCurrencyString(
              "USD",
              formatAmountCompact(Number(lastValuationResult.ltm)),
            )
          : formatCurrencyAmount("USD", lastValuationResult.ltm)
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      label: "Total Earnings",
      value: lastValuationResult?.lifetime_amount
        ? isMd
          ? formatCurrencyString(
              "USD",
              formatAmountCompact(Number(lastValuationResult.lifetime_amount)),
            )
          : formatCurrencyAmount("USD", lastValuationResult.lifetime_amount)
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      label: "Earning Since",
      value: !!lastValuationResult?.first_payment_date
        ? moment(
            lastValuationResult?.first_payment_date,
            DASHED_DATE_FORMAT,
          ).format("YYYY")
        : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      label: "Songs",
      value: lastValuationResult?.track_count || EMPTY_VALUE_PLACEHOLDER,
    },
  ];

  const handleUpdatePassword = async (values: FormValues) => {
    await updateRoyaltyAccountPassword(assetIds[0], values.password);
    queryClient.invalidateQueries("assetKYWRoyaltyAccount");
    history.push(`${routes.sellerValuationLoading()}?asset_id=${assetIds[0]}`);
  };

  return (
    <>
      {isEditRoyaltyAccountModalOpen && (
        <EditRoyaltyAccountModal
          isOpen
          onClose={() => {
            history.push({ search: "" });
            setIsEditRoyaltyAccountModalOpen(false);
          }}
          onAssetEdited={() => {
            queryClient.invalidateQueries("assetKYWRoyaltyAccount");
          }}
          assetIds={assetIds}
          hasDeal={some(assets, "has_deal")}
          username={royaltyAccount.username}
          royaltyPayor={royaltyPayor}
          handleUpdatePassword={handleUpdatePassword}
        />
      )}
      <Card elevation={3} className={classes.card}>
        <Box className={classes.header}>
          <AccountInfo
            royaltyPayorDisplayName={royaltyPayor.display_name}
            username={royaltyAccount?.username}
            subAccountName={royaltyAccount?.subaccount_name}
            connectionStatus={lastAsset.last_scraper_task_status}
          />
          <Box>
            {royaltyAccount && (
              <Box width={24}>
                <IconButton
                  aria-label="more"
                  id="more-button"
                  aria-controls="asset-more-menu"
                  aria-expanded={open ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    setAnchorElement(event.currentTarget);
                  }}
                  size="small"
                  className={classes.editAccountBtn}
                >
                  <SettingsIcon />
                </IconButton>
              </Box>
            )}
            <Menu
              id="asset-more-menu"
              anchorEl={anchorElement}
              open={Boolean(anchorElement)}
              onClose={() => setAnchorElement(null)}
            >
              <MenuItem
                key="see-full-analysis"
                onClick={() => setAnchorElement(null)}
                dense
                className={classes.menuItemLink}
                component={Link}
                to={`${routes.sellerValuationLoading()}?asset_id=${
                  lastAsset.id
                }`}
              >
                See Full Analysis
              </MenuItem>

              {royaltyAccount && (
                <MenuItem
                  key="edit-account"
                  onClick={() => {
                    setAnchorElement(null);
                    setIsEditRoyaltyAccountModalOpen(true);
                  }}
                  dense
                  className={classes.menuItemLink}
                >
                  Edit Royalty Account
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
        {isConnected ? (
          <Link
            to={`${routes.sellerValuationLoading()}?asset_id=${lastAsset.id}`}
          >
            <Box className={classes.actionAreaContainer}>
              <CardActionArea
                action={
                  isConnected && {
                    label: isSmUp ? "View Analysis" : "Analysis",
                  }
                }
                stats={valuationStats}
                lastUpdated={
                  lastValuationResult?.created_at
                    ? moment(lastValuationResult?.created_at).format("MM/DD/YY")
                    : EMPTY_VALUE_PLACEHOLDER
                }
              />
            </Box>
          </Link>
        ) : (
          <>
            {lastAsset.last_scraper_task_status
              .royalty_payor_action_required ? (
              <MuiLink
                href={royaltyPayor.portal_url}
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                <AlertActionArea
                  alertTitle={lastAsset.last_scraper_task_status.message}
                  alertDetails={
                    lastAsset.last_scraper_task_status.fix_instructions
                  }
                  actionMessage="Update Now"
                />
              </MuiLink>
            ) : lastAsset.last_scraper_task_status.edit_credentials_required ? (
              <Box
                className={classes.link}
                onClick={() => setIsEditRoyaltyAccountModalOpen(true)}
              >
                <AlertActionArea
                  alertTitle={lastAsset.last_scraper_task_status.message}
                  alertDetails={
                    lastAsset.last_scraper_task_status.fix_instructions
                  }
                  actionMessage="Update Now"
                />
              </Box>
            ) : (
              <AlertActionArea
                alertTitle={lastAsset.last_scraper_task_status.message}
                alertDetails={lastAsset.last_scraper_task_status.description}
                actionMessage={
                  lastAsset.last_scraper_task_status.edit_credentials_required
                    ? "Update Now"
                    : undefined
                }
              />
            )}
          </>
        )}
      </Card>
    </>
  );
};

export default RoyaltyAccountCard;
